import { kyRequest } from './kyHttp';

/**
 * @typedef {Object} CartItem
 * @property {string} id
 * @property {string} name
 * @property {number} price
 * @property {number} quantity
 * @property {string} [image]
 * @property {string} [itemType]
 * @property {number} [originalPrice]
 * @property {string} [discountText]
 * @property {Object<string, any>} [details]
 */

/**
 * Fetch all items in the cart.
 * @returns {Promise<CartItem[]>}
 */
export async function getCartApi() {
  return kyRequest.get('cart').json();
}

/**
 * Add an item to the cart.
 * @param {CartItem} item
 * @returns {Promise<CartItem[]>}
 */
export async function addToCartApi(item) {
  return kyRequest.post('cart', item).json();
}

/**
 * Update quantity of a cart item.
 * @param {{ id: string, quantity: number }} payload
 * @returns {Promise<CartItem[]>}
 */
export async function updateCartItemApi(payload) {
  return kyRequest.put(`cart/${payload.id}`, payload).json();
}

/**
 * Remove an item from the cart.
 * @param {string} id
 * @returns {Promise<CartItem[]>}
 */
export async function removeFromCartApi(id) {
  return kyRequest.delete(`cart/${id}`).json();
}

/**
 * Clear all items from the cart.
 * @returns {Promise<void>}
 */
export async function clearCartApi() {
  return kyRequest.delete('cart').json();
}

/**
 * Validate guest cart items without saving to database
 * @param {CartItem[]} cartItems - Array of cart items to validate
 * @returns {Promise<Object>} - Validation result with pricing and availability info
 */
export async function validateGuestCartApi(cartItems) {
  const payload = {
    items: cartItems.map((item) => ({
      item_uuid: item.id || item.uuid,
      purchasable_uuid: item.id || item.uuid,
      item_type: item.itemType,
      quantity: item.quantity || 1,
      expected_price: item.price,
    })),
  };

  // kyRequest.post automatically wraps data in { json: data }
  return kyRequest.post('cart/validate', payload).json();
}

/**
 * Alternative validation method using fetch directly for debugging
 * @param {CartItem[]} cartItems - Array of cart items to validate
 * @returns {Promise<Object>} - Validation result
 */
export async function validateGuestCartApiFetch(cartItems) {
  const payload = {
    items: cartItems.map((item) => ({
      item_uuid: item.id || item.uuid,
      purchasable_uuid: item.id || item.uuid,
      item_type: item.itemType,
      quantity: item.quantity || 1,
      expected_price: item.price,
    })),
  };

  const { store } = await import('@/redux/store');
  const { token } = store.getState().auth;

  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cart/validate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`API Error: ${response.status} - ${JSON.stringify(errorData)}`);
  }

  return response.json();
}
